import compose from 'lodash/flowRight'
import * as React from 'react'
import {connect} from 'react-redux'

import {showQueuedBuildsForBranch, hideQueuedBuildsForBranch} from '../../../../../../../actions'
import {withOneLineProp} from '../../../../../../../contexts/oneLine'
import withHook from '../../../../../../../hocs/withHook'
import {useAppSelector} from '../../../../../../../hooks/react-redux'
import {withBuildTypeId} from '../../../../../../../hooks/routes'
import {useBranchesWithBuildsArg} from '../../../../../../../rest/branchesWithBuilds'
import type {StatusKey} from '../../../../../../../rest/schemata'
import {getBuildsHash, getShowQueuedBuildsPerBranch} from '../../../../../../../selectors'
import type {Branch} from '../../../../../../../services/rest'
import {restApi} from '../../../../../../../services/rest'
import type {BuildId, NormalizedBranchWithBuilds, NormalizedBuild} from '../../../../../../../types'
import {emptyArray} from '../../../../../../../utils/empty'
import type {KeyValue} from '../../../../../../../utils/object'

import CollapsibleBranchLine from './CollapsibleBranchLine'

type OwnProps = {
  branchKey: StatusKey
}
type StateProps = {
  branch: Branch | null | undefined
  queuedBuildIds: ReadonlyArray<BuildId>
  nonQueuedBuildIds: ReadonlyArray<BuildId>
  queuedVisible: boolean
}

type ExtractedBuilds = {
  queuedBuildIds: BuildId[]
  nonQueuedBuildIds: BuildId[]
}

const extractBuilds = (
  buildIds: readonly BuildId[],
  buildList: KeyValue<BuildId, NormalizedBuild | null>,
) =>
  buildIds.reduce<ExtractedBuilds>(
    (accumulator, buildId) => {
      if (!buildList[buildId]) {
        return accumulator
      }

      if (buildList[buildId]?.state === 'queued') {
        accumulator.queuedBuildIds.push(buildId)
      } else {
        accumulator.nonQueuedBuildIds.push(buildId)
      }

      return accumulator
    },
    {
      queuedBuildIds: [],
      nonQueuedBuildIds: [],
    },
  )

const extractBranch = ({builds, ...branch}: NormalizedBranchWithBuilds) => branch

const actionCreators = {
  showQueuedBuildsForBranch,
  hideQueuedBuildsForBranch,
}
export default compose(
  withOneLineProp,
  withBuildTypeId,
  connect(null, actionCreators),
  withHook(({branchKey}: OwnProps): StateProps => {
    const arg = useBranchesWithBuildsArg()
    const {branch} = restApi.endpoints.getAllBranchesOfBuildTypeMerged.useQuery(arg, {
      selectFromResult: ({data}) => ({branch: data?.entities.branches?.[branchKey]}),
    })
    const allBuilds = useAppSelector(getBuildsHash)
    const allBranchBuilds = branch?.builds.build ?? emptyArray
    const {queuedBuildIds, nonQueuedBuildIds} = React.useMemo(
      () => extractBuilds(allBranchBuilds, allBuilds),
      [allBranchBuilds, allBuilds],
    )
    const queuedVisible = useAppSelector(state => getShowQueuedBuildsPerBranch(state, branchKey))
    const extractedBranch = React.useMemo(
      () => (branch != null ? extractBranch(branch) : null),
      [branch],
    )
    return {
      branch: extractedBranch,
      queuedBuildIds: queuedBuildIds?.length ? queuedBuildIds : emptyArray,
      nonQueuedBuildIds: nonQueuedBuildIds?.length ? nonQueuedBuildIds : emptyArray,
      queuedVisible: (queuedBuildIds.length > 0 && nonQueuedBuildIds.length === 0) || queuedVisible,
    }
  }),
  React.memo,
)(CollapsibleBranchLine)
